import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  MapPin, User, Clock, RefreshCw, Loader2, Gauge, ArrowDownToLine, Mountain,
  ChevronDown, Drill, Timer, MapPinned, X, Crosshair, AlertTriangle, Trash2, Ban
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { ScopeChip } from './MineScopeSelect'
import Tabs from './Tabs'
import { selectCls, labelCls } from './WizardModal'
import { equipmentOf, PIT_STATUS } from '../equipment'
import { loadGoogleMaps, makeOrthoLayer } from '../lib/maps'
import {
  normalizeHole, hasPosition, materialKind,
  MATERIAL_WASTE, MATERIAL_ORE, MATERIAL_UNKNOWN, HOLE_DONE, HOLE_ABORTED,
} from '../lib/holes'
import { useCodeLabels } from '../lib/catalogs'
import { holeTypeLabel } from '../lib/i18nText'
import MaterialChip from './MaterialChip'
import { fmtNum, fmtInt, fmtDate, fmtDateTime } from '../i18n/format'
import { useAuth } from '../contexts/AuthContext'
import { useConfirm } from './ConfirmDialog'
import { CLEAR_HOLES_CONFIRM, apiError } from '../lib/api'

const ORE_HEX = '#017f96'
const WASTE_HEX = '#f79009'
const UNCLASSIFIED_HEX = '#94a3b8'
const ABORTED_HEX = '#475569'
const DEFAULT_CENTER = { lat: -23.5505, lng: -46.6333 }

const MATERIAL_HEX = {
  [MATERIAL_ORE]: ORE_HEX,
  [MATERIAL_WASTE]: WASTE_HEX,
  [MATERIAL_UNKNOWN]: UNCLASSIFIED_HEX,
}

// O tipo do 0.8 (materialType) decide a cor sozinho; o texto livre do 0.7 passa pelo
// classificador de palavras-chave.
const holeKind = (hole) => materialKind(hole?.materialType, hole?.material)

function holeHex(hole) {
  if (hole?.aborted) return ABORTED_HEX
  return MATERIAL_HEX[holeKind(hole)]
}

function holeTint(hole) {
  if (hole?.aborted) return 'text-slate-400 dark:text-slate-500'
  const cls = holeKind(hole)
  if (cls === MATERIAL_WASTE) return 'text-amber-500'
  if (cls === MATERIAL_ORE) return 'text-brand'
  return 'text-slate-400 dark:text-slate-500'
}

const GRADE_STYLES = {
  high: { text: 'text-status-op', bg: 'bg-emerald-50 dark:bg-emerald-500/10', dot: 'bg-status-op', labelKey: 'production:holes.grade_high' },
  mid:  { text: 'text-status-stop', bg: 'bg-amber-50 dark:bg-amber-500/10', dot: 'bg-status-stop', labelKey: 'production:holes.grade_mid' },
  low:  { text: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-800', dot: 'bg-slate-400', labelKey: 'production:holes.grade_low' },
  neutral: { text: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-800', dot: 'bg-slate-400' },
}

// Teores do catálogo do 0.8 com nível de cor; NAG/PAG (e code novo da mina) ficam neutros.
const GRADE_CODE_LEVEL = { VERY_HIGH_GRADE: 'high', HIGH_GRADE: 'high', MEDIUM_GRADE: 'mid', LOW_GRADE: 'low' }

// Os limiares numéricos são provisórios e dependem do mineral (o teor chega como texto
// livre da HMI, sem unidade); teor alto é o bom resultado, então nenhum nível usa o
// vermelho de alarme reservado a "sem comunicação".
function gradeStyle(grade) {
  if (grade == null || grade === '') return null
  const g = grade.toString().toLowerCase()
  const num = parseFloat(g.replace(',', '.'))
  let level = null
  if (/alto|high|rico|elevad/.test(g)) level = 'high'
  else if (/m[eé]di|medium|mid|inter/.test(g)) level = 'mid'
  else if (/baixo|low|pobre|marginal/.test(g)) level = 'low'
  else if (!Number.isNaN(num)) level = num >= 3 ? 'high' : num >= 1.5 ? 'mid' : 'low'
  if (!level) return { ...GRADE_STYLES.neutral, label: grade.toString() }
  return { ...GRADE_STYLES[level], raw: grade }
}

const hasGrade = (hole) => !!(hole.materialGrading || gradeStyle(hole.oreGrade))

// O teor deixou de ser um passo obrigatório na HMI, então na maioria dos furos vem
// vazio: sem teor o componente não renderiza nada em vez de uma pílula em branco. No
// 0.8 o teor é um código do catálogo e sai com o rótulo traduzido dele.
function GradePill({ hole }) {
  const { t } = useTranslation()
  const labels = useCodeLabels()
  if (hole.materialGrading) {
    const st = GRADE_STYLES[GRADE_CODE_LEVEL[hole.materialGrading] || 'neutral']
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
        {labels.grading(hole.materialGrading, hole.materialType)}
      </span>
    )
  }
  const st = gradeStyle(hole.oreGrade)
  if (!st) return null
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.labelKey ? t(st.labelKey) : st.label}
    </span>
  )
}

// Classificação do 0.8 (código traduzido) ou a rocha em texto livre do 0.7.
function useClassificationText(hole) {
  const labels = useCodeLabels()
  if (hole.materialClassification) return labels.classification(hole.materialClassification, hole.materialType)
  return hole.rockType || ''
}

function HoleMaterial({ hole }) {
  return <MaterialChip type={hole.materialType} material={hole.material} />
}

function AbortedBadge() {
  const { t } = useTranslation()
  return (
    <span title={t('production:holes.aborted_hint')}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
      <Ban size={11} className="shrink-0" /> {t('production:holes.aborted')}
    </span>
  )
}

function fmtDuration(s) {
  if (s == null) return '—'
  s = Math.max(0, Math.round(s))
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60
  return [h, m, sec].map(v => String(v).padStart(2, '0')).join(':')
}

function fmtTime(t) {
  if (!t) return '—'
  return fmtDate(t, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// Coordenada é invariante de idioma: com a vírgula decimal do pt-BR o par lat/lon vira
// "-23,55050, -46,63330" e não dá para copiar nem colar num mapa.
function fmtCoord(v) {
  if (v == null) return '—'
  return Number(v).toFixed(5)
}

// Ortomosaico que cobre a mina do furo (só um por mina fica ativo).
function orthoForMine(maps, mineId) {
  if (mineId == null) return null
  return (maps || []).find(m => m.status === 'ready' && m.active && Number(m.mine_id) === Number(mineId)) || null
}

// Pino simples data-URI colorido pelo material, no lugar da maquinaria de glifos do
// mapa de equipamentos (aqui o marcador é estático, não tem rumo nem velocidade). O furo
// descartado leva pino cinza-escuro com um X no lugar do círculo.
function holePinIcon(gmaps, hole) {
  const color = holeHex(hole)
  const mark = hole?.aborted
    ? '<path d="M9.6 9.1L16.4 15.9M16.4 9.1L9.6 15.9" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round"/>'
    : '<circle cx="13" cy="12.5" r="4.2" fill="#ffffff"/>'
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">
    <path d="M13 33S24.5 20.4 24.5 12.5A11.5 11.5 0 1 0 1.5 12.5C1.5 20.4 13 33 13 33Z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
    ${mark}
  </svg>`
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new gmaps.Size(26, 34),
    anchor: new gmaps.Point(13, 33),
  }
}

function Field({ label, value, children }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 truncate">{label}</p>
      {children || <p className="text-sm text-slate-700 dark:text-slate-200 mt-1 truncate">{value}</p>}
    </div>
  )
}

// Renderiza em <span>: o Metric vive dentro do <button> do card, que só aceita conteúdo
// de frase (um <div> ali é HTML inválido).
function Metric({ icon, label, value, accent }) {
  return (
    <span className="block min-w-0">
      <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">{icon} <span className="truncate">{label}</span></span>
      <span className={`block text-sm font-semibold tabular-nums mt-0.5 truncate ${accent ? 'text-brand-600 dark:text-brand-300' : 'text-slate-700 dark:text-slate-200'}`}>{value}</span>
    </span>
  )
}

// HoleMiniMap: mapa do card expandido, centrado no furo. Mesma forma do MapBoundsEditor
// (loader num efeito, wrapper relative + div absolute inset-0).
function HoleMiniMap({ hole, orthoMap, height = 'h-56' }) {
  const { t } = useTranslation()
  const elRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const orthoRef = useRef(null)
  const [err, setErr] = useState(false)

  // O snapshot do WS reenvia a lista inteira de mapas a cada reconexão, então o objeto do
  // ortomosaico troca de identidade sem mudar de conteúdo: o efeito depende da assinatura
  // (id:updated_at), senão constrói um segundo mapa dentro do mesmo div.
  const orthoSig = orthoMap ? `${orthoMap.id}:${orthoMap.updated_at}` : ''
  const orthoMapRef = useRef(orthoMap)
  orthoMapRef.current = orthoMap

  useEffect(() => {
    let cancelled = false
    loadGoogleMaps().then((gmaps) => {
      if (cancelled || !elRef.current) return
      const center = { lat: hole.lat, lng: hole.lon }
      if (!mapRef.current) {
        mapRef.current = new gmaps.Map(elRef.current, {
          zoom: 18, center, mapTypeId: 'hybrid', disableDefaultUI: true, backgroundColor: '#0b0f17',
        })
      }
      const map = mapRef.current
      map.setCenter(center)

      if (markerRef.current) {
        markerRef.current.setPosition(center)
        markerRef.current.setIcon(holePinIcon(gmaps, hole))
      } else {
        markerRef.current = new gmaps.Marker({
          position: center, map, icon: holePinIcon(gmaps, hole),
          title: hole.holeId || hole.deviceName,
        })
      }

      if (orthoRef.current) {
        const idx = map.overlayMapTypes.getArray().indexOf(orthoRef.current)
        if (idx >= 0) map.overlayMapTypes.removeAt(idx)
        orthoRef.current = null
      }
      const ortho = orthoMapRef.current
      if (ortho) {
        orthoRef.current = makeOrthoLayer(gmaps, ortho)
        map.overlayMapTypes.insertAt(0, orthoRef.current)
      }
    }).catch(e => {
      console.error('[holes] Google Maps:', e)
      if (!cancelled) setErr(true)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hole.id, hole.lat, hole.lon, hole.material, hole.materialType, hole.aborted, hole.holeId, hole.deviceName, orthoSig])

  useEffect(() => () => {
    const gmaps = window.google?.maps
    const map = mapRef.current
    if (markerRef.current) {
      gmaps?.event.clearInstanceListeners(markerRef.current)
      markerRef.current.setMap(null)
      markerRef.current = null
    }
    if (map && orthoRef.current) {
      const idx = map.overlayMapTypes.getArray().indexOf(orthoRef.current)
      if (idx >= 0) map.overlayMapTypes.removeAt(idx)
    }
    orthoRef.current = null
    if (map) gmaps?.event.clearInstanceListeners(map)
    mapRef.current = null
    elRef.current?.replaceChildren()
  }, [])

  return (
    <div className={`relative w-full ${height} rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700`}>
      <div ref={elRef} className="absolute inset-0" />
      {err && (
        <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800">
          {t('ops:map.check_api_key')} <code className="ml-1">.env</code>
        </div>
      )}
    </div>
  )
}

function HoleDetail({ hole, orthoMap }) {
  const { t, i18n } = useTranslation()
  const eq = hole.eq
  const classification = useClassificationText(hole)
  const hasMaterial = !!(hole.materialType || hole.material)

  return (
    <div className="px-5 py-4 grid gap-4 lg:grid-cols-2">
      <div className="space-y-3 min-w-0">
        {hole.aborted && (
          <p className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <Ban size={13} className="shrink-0 mt-0.5" /> {t('production:holes.aborted_hint')}
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          <Field label={t('common:entities.pit')} value={hole.pitName || '—'} />
          <Field label={t('production:holes.hole_type')} value={holeTypeLabel(hole.holeType, i18n.language) || '—'} />
          <Field label={t('common:entities.operator')} value={hole.operatorName || t('production:holes.no_operator')} />
          <Field label={t('common:entities.material')}>
            <div className="mt-1 flex min-w-0">
              {hasMaterial ? <HoleMaterial hole={hole} /> : <span className="text-sm text-slate-400 dark:text-slate-500">—</span>}
            </div>
          </Field>
          <Field label={hole.materialClassification ? t('production:holes.classification') : t('production:holes.rock_type')} value={classification || '—'} />
          <Field label={t('production:holes.grade')}>
            <div className="mt-1">
              {hasGrade(hole) ? <GradePill hole={hole} /> : <span className="text-sm text-slate-400 dark:text-slate-500">—</span>}
            </div>
          </Field>
          <Field label={t('production:holes.started')} value={fmtDateTime(hole.startedAt)} />
          <Field label={t('production:holes.finished')} value={fmtDateTime(hole.completedAt)} />
          <Field label={t('production:holes.duration')}>
            <p className="text-sm text-slate-700 dark:text-slate-200 mt-1 tabular-nums">{fmtDuration(hole.durationSeconds)}</p>
          </Field>
          <Field label={t('production:holes.depth')}>
            <p className="text-sm text-slate-700 dark:text-slate-200 mt-1 tabular-nums">
              {hole.depthM != null ? `${fmtNum(hole.depthM)} ${t('common:units.meters')}` : '—'}
            </p>
          </Field>
          <Field label={t('production:holes.rate')}>
            <p className="text-sm text-slate-700 dark:text-slate-200 mt-1 tabular-nums">
              {hole.metersPerHour != null ? `${fmtNum(hole.metersPerHour)} ${t('common:units.meters_per_hour')}` : '—'}
            </p>
          </Field>
          <Field label={t('common:entities.equipment')} value={eq.name || hole.deviceName} />
        </div>

        {hole.characteristics.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5">{t('production:holes.characteristics')}</p>
            <div className="flex flex-wrap gap-1.5">
              {hole.characteristics.map((c, i) => (
                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px]">{c}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5">{t('production:holes.position')}</p>
        {hasPosition(hole) ? (
          <>
            <HoleMiniMap hole={hole} orthoMap={orthoMap} />
            <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">{fmtCoord(hole.lat)}, {fmtCoord(hole.lon)}</p>
          </>
        ) : (
          <div className="h-56 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500">
            <MapPin size={24} className="text-slate-300 dark:text-slate-600" />
            <p className="text-xs px-6 text-center">{t('production:holes.no_position')}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function HoleCard({ hole, orthoMap, expanded, onToggle }) {
  const { t, i18n } = useTranslation()
  const classification = useClassificationText(hole)
  const eq = hole.eq
  const depth = hole.depthM != null ? `${fmtNum(hole.depthM)} ${t('common:units.meters')}` : '—'
  const rate = hole.metersPerHour != null ? `${fmtNum(hole.metersPerHour)} ${t('common:units.meters_per_hour')}` : '—'
  const place = [hole.mineName || eq.mine, hole.pitName || eq.pit].filter(Boolean).join(' · ') || '—'
  const detailId = `hole-detail-${hole.id}`

  // Tudo dentro do <button> é <span>: o botão só aceita conteúdo de frase, e <div>/<p>
  // ali dentro é HTML inválido (o navegador reestrutura a árvore).
  return (
    <div className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-slate-300 transition-all overflow-hidden ${expanded ? 'md:col-span-2 2xl:col-span-3' : ''}`}>
      <button onClick={onToggle} aria-expanded={expanded} aria-controls={detailId} className="w-full text-left px-5 py-4">
        <span className="flex items-start gap-3">
          <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0">
            <Drill size={20} className={holeTint(hole)} />
          </span>
          <span className="block min-w-0 flex-1">
            <span className="block text-lg font-semibold text-slate-800 dark:text-slate-100 leading-tight truncate">{hole.holeId || t('common:entities.hole')}</span>
            <span className="block text-xs text-slate-400 dark:text-slate-500 truncate">{eq.name || hole.deviceName}{eq.label ? ` · ${eq.label}` : ''}</span>
          </span>
          <ChevronDown size={18} className={`text-slate-300 dark:text-slate-600 shrink-0 mt-1 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </span>

        <span className="mt-3 flex items-center gap-1.5 flex-wrap">
          {hole.aborted && <AbortedBadge />}
          {hole.holeType && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{holeTypeLabel(hole.holeType, i18n.language)}</span>
          )}
          <HoleMaterial hole={hole} />
          {classification && (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400"><Mountain size={11} className="shrink-0" /> {classification}</span>
          )}
          <GradePill hole={hole} />
        </span>

        <span className="mt-3 grid grid-cols-3 gap-3 border-t border-slate-100 dark:border-slate-800 pt-3">
          <Metric icon={<ArrowDownToLine size={11} />} label={t('production:holes.depth_abbr')} value={depth} accent />
          <Metric icon={<Gauge size={11} />} label={t('production:holes.rate')} value={rate} />
          <Metric icon={<Timer size={11} />} label={t('production:holes.duration')} value={fmtDuration(hole.durationSeconds)} />
        </span>

        <span className="mt-3 flex items-center gap-3 flex-wrap text-xs text-slate-400 dark:text-slate-500">
          <span className="inline-flex items-center gap-1 min-w-0"><User size={11} className="shrink-0" /> <span className="truncate">{hole.operatorName || t('production:holes.no_operator')}</span></span>
          <span className="inline-flex items-center gap-1 min-w-0"><MapPin size={11} className="shrink-0" /> <span className="truncate">{place}</span></span>
          <span className="inline-flex items-center gap-1 ml-auto"><Clock size={11} className="shrink-0" /> {fmtTime(hole.completedAt)}</span>
        </span>
      </button>

      {expanded && (
        <div id={detailId} className="border-t border-slate-100 dark:border-slate-800">
          <HoleDetail hole={hole} orthoMap={orthoMap} />
        </div>
      )}
    </div>
  )
}

const STATUS_FILTERS = ['all', HOLE_DONE, HOLE_ABORTED]

function StatusFilter({ value, onChange }) {
  const { t } = useTranslation()
  const label = { all: 'status_all', [HOLE_DONE]: 'status_done', [HOLE_ABORTED]: 'status_aborted' }
  return (
    <label className="block w-40">
      <span className={labelCls}>{t('production:holes.status_filter')}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className={selectCls}>
        {STATUS_FILTERS.map(k => <option key={k} value={k}>{t(`production:holes.${label[k]}`)}</option>)}
      </select>
    </label>
  )
}

function PitFilter({ pits, value, onChange }) {
  const { t } = useTranslation()
  return (
    <label className="block w-52">
      <span className={labelCls}>{t('common:entities.pit')}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className={selectCls}>
        <option value="">{t('production:holes.all_pits')}</option>
        {pits.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </label>
  )
}

function SelectedHoleChips({ hole }) {
  const classification = useClassificationText(hole)
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {hole.aborted && <AbortedBadge />}
      <HoleMaterial hole={hole} />
      {classification && <span className="inline-flex items-center gap-1"><Mountain size={11} /> {classification}</span>}
    </div>
  )
}

// HolesMap: todos os furos do filtro atual como marcadores. Fica sempre montado (a aba
// só o esconde com `hidden`), então precisa do resize do Maps ao voltar a aparecer.
function HolesMap({ holes, maps, visible }) {
  const { t } = useTranslation()
  const elRef = useRef(null)
  const nsRef = useRef(null)
  const gmapRef = useRef(null)
  const markersRef = useRef([])
  const orthoRef = useRef([])
  const orthoSigRef = useRef('')
  const didFitRef = useRef(false)
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const positioned = useMemo(() => holes.filter(hasPosition), [holes])
  const omitted = holes.length - positioned.length
  const unclassified = useMemo(
    () => positioned.filter(h => !h.aborted && holeKind(h) === MATERIAL_UNKNOWN).length,
    [positioned])
  const abortedOnMap = useMemo(() => positioned.filter(h => h.aborted).length, [positioned])
  const selected = selectedId != null ? positioned.find(h => h.id === selectedId) || null : null

  // O enquadramento lê os furos por ref, então `fit` é estável e o efeito de resize não
  // volta a disparar a cada refetch (era isso que jogava o operador fora do zoom dele).
  const positionedRef = useRef(positioned)
  positionedRef.current = positioned
  const visibleRef = useRef(visible)
  visibleRef.current = visible

  useEffect(() => {
    let cancelled = false
    loadGoogleMaps().then((gmaps) => {
      if (cancelled) return
      nsRef.current = gmaps
      setReady(true)
    }).catch(e => {
      console.error('[holes] Google Maps:', e)
      if (!cancelled) setErr(true)
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const gmaps = nsRef.current
    if (!ready || !gmaps || !elRef.current || gmapRef.current) return
    gmapRef.current = new gmaps.Map(elRef.current, {
      zoom: 14, center: DEFAULT_CENTER, mapTypeId: 'hybrid',
      fullscreenControl: false, streetViewControl: false, mapTypeControl: false, backgroundColor: '#0b0f17',
    })
  }, [ready])

  // Ortomosaicos em overlayMapTypes (pane overlayLayer, abaixo dos marcadores).
  useEffect(() => {
    const gmaps = nsRef.current, map = gmapRef.current
    if (!gmaps || !map) return
    const active = (maps || []).filter(m => m.status === 'ready' && m.active)
    const sig = active.map(m => `${m.id}:${m.updated_at}`).join('|')
    if (sig === orthoSigRef.current) return
    orthoSigRef.current = sig
    orthoRef.current.forEach(layer => {
      const idx = map.overlayMapTypes.getArray().indexOf(layer)
      if (idx >= 0) map.overlayMapTypes.removeAt(idx)
    })
    orthoRef.current = active.map(m => {
      const layer = makeOrthoLayer(gmaps, m)
      map.overlayMapTypes.insertAt(0, layer)
      return layer
    })
  }, [ready, maps])

  const fit = useCallback(() => {
    const gmaps = nsRef.current, map = gmapRef.current
    const pts = positionedRef.current
    if (!gmaps || !map || pts.length === 0) return
    if (pts.length === 1) {
      map.setCenter({ lat: pts[0].lat, lng: pts[0].lon })
      map.setZoom(17)
      return
    }
    const bounds = new gmaps.LatLngBounds()
    pts.forEach(h => bounds.extend({ lat: h.lat, lng: h.lon }))
    map.fitBounds(bounds, 80)
  }, [])

  const clearMarkers = useCallback(() => {
    const gmaps = nsRef.current
    markersRef.current.forEach(m => {
      gmaps?.event.clearInstanceListeners(m)
      m.setMap(null)
    })
    markersRef.current = []
  }, [])

  useEffect(() => {
    const gmaps = nsRef.current, map = gmapRef.current
    if (!gmaps || !map) return
    clearMarkers()
    markersRef.current = positioned.map(h => {
      const marker = new gmaps.Marker({
        position: { lat: h.lat, lng: h.lon }, map,
        icon: holePinIcon(gmaps, h),
        title: h.holeId || h.deviceName, zIndex: h.aborted ? 5 : 10,
      })
      marker.addListener('click', () => setSelectedId(h.id))
      return marker
    })
    // Enquadra na primeira vez que há furos com o mapa à vista (o filtro pode carregar
    // depois da aba abrir); daí em diante o enquadramento é só pelo botão.
    if (visibleRef.current && !didFitRef.current && positioned.length > 0) {
      fit()
      didFitRef.current = true
    }
  }, [ready, positioned, fit, clearMarkers])

  // Enquanto a aba está escondida o div tem altura zero e o Maps desenha em branco;
  // ao reaparecer é preciso um resize. O efeito depende só da visibilidade: um refetch
  // (hole_done de qualquer perfuratriz, Atualizar, edição no cadastro) não pode
  // reenquadrar o mapa por cima do zoom que o operador deu na bancada.
  useEffect(() => {
    if (!visible || !gmapRef.current) return
    const id = setTimeout(() => {
      nsRef.current?.event.trigger(gmapRef.current, 'resize')
      if (didFitRef.current || positionedRef.current.length === 0) return
      fit()
      didFitRef.current = true
    }, 100)
    return () => clearTimeout(id)
  }, [visible, fit])

  useEffect(() => () => {
    const gmaps = nsRef.current, map = gmapRef.current
    clearMarkers()
    if (map) {
      orthoRef.current.forEach(layer => {
        const idx = map.overlayMapTypes.getArray().indexOf(layer)
        if (idx >= 0) map.overlayMapTypes.removeAt(idx)
      })
      gmaps?.event.clearInstanceListeners(map)
    }
    orthoRef.current = []
    orthoSigRef.current = ''
    gmapRef.current = null
    // O Maps escreve os próprios nós dentro do div (o React não os conhece); limpar aqui
    // garante que uma remontagem construa o mapa num container vazio.
    elRef.current?.replaceChildren()
  }, [clearMarkers])

  return (
    <div className="h-full relative">
      <div ref={elRef} className="w-full h-full" />

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-900 text-slate-400 dark:text-slate-500">
          {err
            ? <span className="text-sm">{t('ops:map.check_api_key')} <code>.env</code></span>
            : <><Loader2 size={20} className="animate-spin mr-2" /> {t('production:holes.loading')}</>}
        </div>
      )}

      {ready && positioned.length > 0 && (
        <button onClick={fit} title={t('ops:map.center_on_map')} aria-label={t('ops:map.center_on_map')}
          className="absolute bottom-6 right-3 p-2 rounded-lg bg-white/85 dark:bg-slate-900/85 backdrop-blur shadow-md border border-black/5 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:text-brand-600 dark:hover:text-brand-300">
          <Crosshair size={16} />
        </button>
      )}

      {ready && (
        <div className="absolute top-3 left-3 bg-white/85 dark:bg-slate-900/85 backdrop-blur rounded-lg shadow-md border border-black/5 dark:border-white/10 px-3 py-2 text-[11px] text-slate-700 dark:text-slate-200 pointer-events-none">
          <p className="font-semibold text-brand-600 dark:text-brand-300 mb-1.5">{t('common:entities.material')}</p>
          <ul className="space-y-1">
            <li className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0 ring-1 ring-black/10" style={{ backgroundColor: ORE_HEX }} />
              {t('production:kpis.ore')}
            </li>
            <li className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0 ring-1 ring-black/10" style={{ backgroundColor: WASTE_HEX }} />
              {t('production:kpis.waste')}
            </li>
            {unclassified > 0 && (
              <li className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0 ring-1 ring-black/10" style={{ backgroundColor: UNCLASSIFIED_HEX }} />
                {t('production:holes.legend_unclassified')}
              </li>
            )}
            {abortedOnMap > 0 && (
              <li className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0 ring-1 ring-black/10" style={{ backgroundColor: ABORTED_HEX }} />
                {t('production:holes.aborted')}
              </li>
            )}
          </ul>
          {omitted > 0 && (
            <p className="mt-2 pt-2 border-t border-black/5 dark:border-white/10 text-slate-500 dark:text-slate-400">
              {t('production:holes.omitted_from_map', { count: omitted })}
            </p>
          )}
        </div>
      )}

      {ready && positioned.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-300 dark:text-slate-500 pointer-events-none">
          <MapPinned size={40} />
          <p className="text-sm">{t('production:holes.map_empty')}</p>
        </div>
      )}

      {selected && (
        <div className="absolute top-3 right-3 w-72 bg-slate-900/90 backdrop-blur rounded-xl shadow-xl border border-white/10 text-slate-200 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-white/10 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-100 truncate">{selected.holeId || t('common:entities.hole')}</p>
              <p className="text-[11px] text-slate-400 truncate">{selected.eq.name || selected.deviceName}</p>
            </div>
            <button onClick={() => setSelectedId(null)} title={t('common:actions.close')} className="text-slate-400 hover:text-slate-200 shrink-0"><X size={16} /></button>
          </div>
          <div className="px-3 py-3 space-y-2 text-[11px] text-slate-300">
            <SelectedHoleChips hole={selected} />
            <div className="grid grid-cols-2 gap-2">
              <span className="flex items-center gap-1.5"><ArrowDownToLine size={12} className="text-slate-500" />
                <span className="tabular-nums">{selected.depthM != null ? `${fmtNum(selected.depthM)} ${t('common:units.meters')}` : '—'}</span></span>
              <span className="flex items-center gap-1.5"><Gauge size={12} className="text-slate-500" />
                <span className="tabular-nums">{selected.metersPerHour != null ? `${fmtNum(selected.metersPerHour)} ${t('common:units.meters_per_hour')}` : '—'}</span></span>
              <span className="flex items-center gap-1.5"><Timer size={12} className="text-slate-500" />
                <span className="tabular-nums">{fmtDuration(selected.durationSeconds)}</span></span>
              <span className="flex items-center gap-1.5"><User size={12} className="text-slate-500" />
                <span className="truncate">{selected.operatorName || t('production:holes.no_operator')}</span></span>
            </div>
            <p className="flex items-center gap-1.5 text-slate-400"><Clock size={12} className="text-slate-500" /> {fmtTime(selected.completedAt)}</p>
            <p className="flex items-center gap-1.5 text-slate-400 tabular-nums"><MapPin size={12} className="text-slate-500" /> {fmtCoord(selected.lat)}, {fmtCoord(selected.lon)}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default function HolesView() {
  const { t } = useTranslation()
  const { devices, categories, mines, maps, holes: liveHoles } = useWS()
  const { mineQS, mineScope, scopeLabel } = useScope()
  const { can } = useAuth()
  const [confirmDialog, ask] = useConfirm()
  const [holes, setHoles] = useState(null)
  const [loadErr, setLoadErr] = useState(false)
  const [pitId, setPitId] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [tab, setTab] = useState('cards')
  const [expanded, setExpanded] = useState(null)
  const reqRef = useRef(0)

  // Espelho do expurgo de ciclos: mesma frase digitada, mesmo escopo de mina e mesmo
  // papel. Desde 2026-08-19 apagar o equipamento NÃO leva os furos junto, então este
  // botão passou a ser o único caminho para removê-los.
  const handleClear = useCallback(() => {
    const scoped = !!mineQS
    ask({
      title: t('production:holes.clear_title'),
      message: scoped
        ? t('production:holes.clear_message_scoped', { mine: scopeLabel })
        : t('production:holes.clear_message'),
      confirmWord: t('production:holes.clear_word'),
      confirmLabel: t('common:actions.clear'),
      danger: true,
      onConfirm: async () => {
        const res = await fetch(`/api/holes?${mineQS ? mineQS + '&' : ''}confirm=${encodeURIComponent(CLEAR_HOLES_CONFIRM)}`,
          { method: 'DELETE' })
        if (res.status === 403) throw new Error(t('production:holes.clear_disabled'))
        if (!res.ok) throw await apiError(res)
        setHoles([])
      },
    })
  }, [ask, t, mineQS, scopeLabel])

  const load = useCallback(() => {
    // A resposta lenta de um filtro antigo não pode sobrescrever a do filtro atual.
    const seq = ++reqRef.current
    const qs = ['limit=200']
    if (mineQS) qs.push(mineQS)
    if (pitId) qs.push(`pit_id=${encodeURIComponent(pitId)}`)
    if (statusFilter !== 'all') qs.push(`status=${statusFilter}`)
    fetch(`/api/holes?${qs.join('&')}`)
      .then(r => { if (!r.ok) throw new Error(`http ${r.status}`); return r.json() })
      .then(d => {
        if (seq !== reqRef.current) return
        setLoadErr(false)
        setHoles(Array.isArray(d) ? d.map(normalizeHole) : [])
      })
      .catch(e => {
        console.error('[holes] GET /api/holes:', e)
        if (seq !== reqRef.current) return
        setLoadErr(true)
        setHoles([])
      })
  }, [mineQS, pitId, statusFilter])

  useEffect(() => { load() }, [load])

  // Um furo novo chega pelo WS (hole_done); o refetch reaplica os filtros de mina/Pit
  // no servidor em vez de duplicar essa regra no cliente. A ref nasce com o topo do
  // buffer para a montagem não disparar um segundo fetch por cima do inicial. O status
  // entra na chave: o mesmo furo reentregue como descartado também refaz a busca.
  const lastLiveKey = liveHoles.length ? `${liveHoles[0].id}:${liveHoles[0].status}` : null
  const seenLiveRef = useRef(lastLiveKey)
  useEffect(() => {
    if (lastLiveKey == null || seenLiveRef.current === lastLiveKey) return
    seenLiveRef.current = lastLiveKey
    load()
  }, [lastLiveKey, load])

  // Só pits ativos, como no documento de pits que vai ao VMC (backend pits.go): um pit
  // exaurido ou apenas planejado não filtra furo nenhum.
  const pitOptions = useMemo(() => {
    if (mineScope === 'none') return []
    const scoped = typeof mineScope === 'number' ? mines.filter(m => Number(m.id) === mineScope) : mines
    return scoped
      .flatMap(m => (Array.isArray(m.pits) ? m.pits : []))
      .filter(p => p.status === PIT_STATUS[0])
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [mines, mineScope])

  useEffect(() => {
    if (pitId && !pitOptions.some(p => String(p.id) === pitId)) setPitId('')
  }, [pitOptions, pitId])

  // devices muda de identidade a cada evento do WS; ler pela ref evita refazer o
  // enriquecimento (e, com ele, os marcadores do mapa) a cada telemetria.
  const devicesRef = useRef(devices)
  devicesRef.current = devices
  // A mina do furo é a que ficou carimbada no INSERT; a do device é só o fallback dos
  // furos antigos (o equipamento pode ter sido remanejado para outra mina depois).
  // O filtro de status também roda aqui: um backend anterior ao 0.8 ignora o ?status=.
  const enriched = useMemo(() => (holes || [])
    .filter(h => statusFilter === 'all' || h.status === statusFilter)
    .map(h => {
      const dev = devicesRef.current.find(d => d.name === h.deviceName) || { name: h.deviceName }
      const eq = equipmentOf(dev, categories, mines)
      const mineId = h.mineId ?? dev.mineId ?? null
      const mine = mineId != null ? mines.find(m => Number(m.id) === Number(mineId)) : null
      return { ...h, eq, mineId, mineName: mine?.name || eq.mine }
    }), [holes, categories, mines, statusFilter])

  // Furo descartado não entra em contagem nem em metros (o backend já manda depth nulo,
  // mas a regra não pode depender disso).
  const doneCount = useMemo(() => enriched.filter(h => !h.aborted).length, [enriched])
  const abortedCount = enriched.length - doneCount
  const totalMeters = useMemo(() => enriched.reduce((a, h) => a + (h.aborted ? 0 : (h.depthM || 0)), 0), [enriched])

  const tabs = [
    { id: 'cards', label: t('production:holes.tab_cards') },
    { id: 'map', label: t('production:holes.tab_map') },
  ]

  if (holes === null) {
    return (
      <div className="h-full flex items-center justify-center bg-offwhite dark:bg-slate-950 text-slate-400 dark:text-slate-500">
        <Loader2 size={22} className="animate-spin mr-2" /> {t('production:holes.loading')}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-offwhite dark:bg-slate-950">
      {confirmDialog}
      {/* Sem border-b aqui: quem fecha o bloco branco é a borda das abas logo abaixo. */}
      <div className="bg-white dark:bg-slate-900 px-6 py-3 flex flex-wrap items-end gap-4">
        <div className="mr-auto min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{t('common:entities.holes')}</h2>
            <ScopeChip />
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
            {statusFilter !== HOLE_ABORTED && <span>{t('production:holes.completed', { count: doneCount })}</span>}
            {abortedCount > 0 && (
              <span className="flex items-center gap-1 text-slate-400 dark:text-slate-500" title={t('production:holes.aborted_hint')}>
                <Ban size={13} className="shrink-0" /> {t('production:holes.aborted_count', { count: abortedCount })}
              </span>
            )}
            {totalMeters > 0 && (
              <span className="flex items-center gap-1.5">
                <ArrowDownToLine size={14} className="text-brand" />
                <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">{fmtInt(totalMeters)} {t('common:units.meters')}</span>
                <span className="text-slate-400 dark:text-slate-500">{t('production:holes.drilled_suffix')}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />
          <PitFilter pits={pitOptions} value={pitId} onChange={setPitId} />
          <button onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            <RefreshCw size={13} /> {t('common:actions.refresh')}
          </button>
          <button
            onClick={handleClear}
            disabled={enriched.length === 0 || !can('admin')}
            title={!can('admin') ? t('admin:perms.needs_admin') : undefined}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-status-down/40 text-status-down hover:bg-status-down/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <Trash2 size={13} /> {t('common:actions.clear')}
          </button>
        </div>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} idPrefix="holes" />

      <div className="flex-1 min-h-0 relative">
        <div role="tabpanel" id="holes-panel-cards" aria-labelledby="holes-tab-cards"
          className={`absolute inset-0 overflow-y-auto p-6 scrollbar-thin ${tab === 'cards' ? '' : 'hidden'}`}>
          {loadErr ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500">
              <AlertTriangle size={40} className="mb-3 text-status-stop" />
              <p className="text-sm">{t('common:states.error')}</p>
              <button onClick={load}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                <RefreshCw size={13} /> {t('common:actions.retry')}
              </button>
            </div>
          ) : enriched.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500">
              <ArrowDownToLine size={40} className="mb-3 text-slate-300 dark:text-slate-600" />
              <p className="text-sm">{statusFilter !== 'all' ? t('production:holes.empty_filtered') : t('production:holes.empty')}</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {enriched.map(h => (
                <HoleCard
                  key={h.id}
                  hole={h}
                  orthoMap={orthoForMine(maps, h.mineId ?? (typeof mineScope === 'number' ? mineScope : null))}
                  expanded={expanded === h.id}
                  onToggle={() => setExpanded(expanded === h.id ? null : h.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* O mapa nunca é desmontado: sair da aba só o esconde, senão o Maps perde o estado. */}
        <div role="tabpanel" id="holes-panel-map" aria-labelledby="holes-tab-map"
          className={`absolute inset-0 ${tab === 'map' ? '' : 'hidden'}`}>
          <HolesMap holes={enriched} maps={maps} visible={tab === 'map'} />
        </div>
      </div>
    </div>
  )
}
